import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./LoginPage.css";
import APP_VERSION from "../version";

function LoginPage({ setCurrentUser }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [emailProvider, setEmailProvider] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [infoMessage, setInfoMessage] = useState(""); // ✅ Holds success confirmation
  const navigate = useNavigate();

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    if (window.FB) window.FB.XFBML.parse();
  }, []);

  const evaluatePasswordStrength = (pwd) => {
    const lengthCriteria = pwd.length >= 8;
    const upperCase = /[A-Z]/.test(pwd);
    const number = /[0-9]/.test(pwd);
    const special = /[^A-Za-z0-9]/.test(pwd);
    const passed = [lengthCriteria, upperCase, number, special].filter(Boolean).length;

    if (!lengthCriteria) return "Weak";
    if (passed === 2 || passed === 3) return "Medium";
    if (passed === 4) return "Strong";
    return "Weak";
  };

  const handleEmailBlur = async () => {
    if (!email) return;
    try {
      const res = await fetch(`/api/users/provider?email=${encodeURIComponent(email)}`, { credentials: "include" });
      const data = await res.json();

      if (res.ok && data.provider) {
        setEmailProvider(data.provider);

        if (data.provider === "google") {
          setErrorMessage(
            "It looks like you have used this email address with Google sign in previously. Please use the ‘Sign in with Google’ button."
          );
        } else if (isSignup && data.provider === "local") {
          setErrorMessage(
            <span>
              An account already exists with this email.{" "}
              <a href="/forgot-password" className="reset-link">here</a>{" "}
              if you would like to reset your password.
            </span>
          );
        } else {
          setErrorMessage("");
        }
      } else {
        setEmailProvider(null);
        setErrorMessage("");
      }
    } catch (err) {
      setEmailProvider(null);
      setErrorMessage("Error checking email. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (isSignup) {
      if (password !== confirmPassword) {
        setErrorMessage("Passwords do not match");
        return;
      }
      if (password.length < 8) {
        setErrorMessage("Password must be at least 8 characters");
        return;
      }

      try {
        const res = await fetch(`/api/users/exists?email=${encodeURIComponent(email)}`, {
          credentials: "include"
        });
        const data = await res.json();

        if (res.ok && data.exists) {
          const providerRes = await fetch(`/api/users/provider?email=${encodeURIComponent(email)}`, {
            credentials: "include"
          });
          const providerData = await providerRes.json();

          if (providerData.provider === "google") {
            setErrorMessage(
              "It looks like you have used this email address with Google sign in previously. Please use the ‘Sign in with Google’ button."
            );
            return;
          } else if (providerData.provider === "local") {
            setErrorMessage(
              <span>
                An account already exists with this email.{" "}
                <a href="/forgot-password" className="reset-link">here</a>{" "}
                if you would like to reset your password.
              </span>
            );
            return;
          }
        }

        // ✅ Send email confirmation instead of creating the account
        const confirmRes = await fetch("/auth/request-signup", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, name: email }),
        });

        const confirmData = await confirmRes.json();
        if (!confirmRes.ok) throw new Error(confirmData.error || "Signup request failed");

        setInfoMessage("✅ A confirmation link has been sent to your email address.");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setPasswordStrength("");
      } catch (err) {
        console.error("❌ Signup error:", err);
        setErrorMessage("Could not send confirmation email. Please try again.");
      }
      return;
    }

    // ✅ Standard login
    try {
      const res = await fetch("/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");

      setCurrentUser(data.user);
      navigate("/");
    } catch (err) {
      console.error("❌ Login error:", err);
      setErrorMessage("Login failed, please reset your password or create an account");
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = "https://campwithfriends.com/auth/google";
  };

  return (
    <div className="login-container" style={{ backgroundColor: "#2c7373" }}>
      <div className="login-box">
        <img
          src="/campwithfriends-logo.png"
          alt="Camp With Friends Logo"
          style={{
            width: "200px",
            marginBottom: "1rem",
          }}
        />

        {/* ✅ If infoMessage, show ONLY this */}
        {infoMessage ? (
          <div style={{ fontSize: "18px", color: "#ffffff", textAlign: "center", marginTop: "20px" }}>
            {infoMessage}
          </div>
        ) : (
          <>
            <h2>{isSignup ? "Create an Account" : "Welcome"}</h2>

            <form onSubmit={handleSubmit} className="login-form">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailProvider(null);
                }}
                onBlur={handleEmailBlur}
                required
              />
              <input
                type="password"
                placeholder={isSignup ? "Create a password" : "Password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (isSignup) setPasswordStrength(evaluatePasswordStrength(e.target.value));
                }}
                required
              />

              {isSignup && (
                <>
                  <input
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {password !== confirmPassword && confirmPassword.length > 0 && (
                    <div className="error-text">Passwords do not match</div>
                  )}
                  {password && (
                    <div className={`strength-text ${passwordStrength.toLowerCase()}`}>
                      Strength: {passwordStrength}
                    </div>
                  )}
                </>
              )}

              <button
                type="submit"
                disabled={
                  !isEmailValid ||
                  (isSignup &&
                    (passwordStrength === "Weak" || passwordStrength === "" || password !== confirmPassword))
                }
                className={
                  !isEmailValid ||
                  (isSignup &&
                    (passwordStrength === "Weak" || passwordStrength === "" || password !== confirmPassword))
                    ? "disabled-button"
                    : ""
                }
              >
                {isSignup ? "Sign up" : "Continue"}
              </button>

              {errorMessage && <div className="soft-error-text">{errorMessage}</div>}
            </form>

            {!isSignup && (
              <div className="forgot-password-text">
              <Link to="/forgot-password" className="contact-link">Forgot your password?</Link>
            </div>
            )}

            <div className="signup-text">
              {isSignup ? (
                <>
                  Already have an account?{" "}
                  <button type="button" onClick={() => setIsSignup(false)}>
                    Log in
                  </button>
                </>
              ) : (
                <>
                  Don’t have an account?{" "}
                  <button type="button" onClick={() => setIsSignup(true)}>
                    Sign up
                  </button>
                </>
              )}
            </div>

            <div className="divider">
              <span>OR</span>
            </div>

            <button className="gsi-material-button" onClick={handleGoogleLogin}>
            <div className="gsi-material-button-icon">
              <img
                src="/google-icon.svg"
                alt="Google"
                style={{ width: "25px", height: "25px" }}
              />
            </div>
              <span className="gsi-material-button-contents">
                Sign in with Google
              </span>
            </button>

            <div className="version-text">
              <span>
                Version: <strong>{APP_VERSION}</strong>
              </span>
              <span style={{ marginLeft: "16px" }}>
                <Link to="/contact" className="contact-link">Contact us</Link>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
